import { useEffect } from "react";
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Radio, Row, Select, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { ZERO_ACTIONS, type ConfigDto, SUPPLIER_RATING_RULES } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useSaveConfig } from "../../api/admin";
import { useConfig } from "../../api/plan";

export function ConfigTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const cfg = useConfig();
  const save = useSaveConfig();
  const [form] = Form.useForm<ConfigDto>();
  const w = Form.useWatch("weights", form);
  const zr = Form.useWatch("zeroRule", form);
  useEffect(() => {
    if (cfg.data) form.setFieldsValue(cfg.data);
  }, [cfg.data, form]);
  const sum = w ? (w.S ?? 0) + (w.H ?? 0) + (w.E ?? 0) : 0;

  const submit = async (v: ConfigDto) => {
    try {
      // sensitive-ok: invio del form; responsabile di funzione interno, non un fornitore
      await save.mutateAsync({ ...v, functionHead: { name: v.functionHead?.name ?? "", email: v.functionHead?.email ?? "" }, managerEsc: !!v.managerEsc });
      void message.success(t("saved"));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };
  const num = (name: (string | number)[], label: string, max: number) => (
    <Col xs={8}>
      <Form.Item name={name} label={label} rules={[{ required: true, message: t("required") }]}>
        <InputNumber min={0} max={max} style={{ width: "100%" }} />
      </Form.Item>
    </Col>
  );

  return (
    <Form form={form} layout="vertical" onFinish={submit} disabled={cfg.isLoading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={`${t("weight")} ${t("a_perSection")}`}>
            <Row gutter={12}>
              {num(["weights", "S"], t("sec_S"), 100)}
              {num(["weights", "H"], t("sec_H"), 100)}
              {num(["weights", "E"], t("sec_E"), 100)}
            </Row>
            <Alert type={sum === 100 ? "success" : "warning"} showIcon message={`${t("a_total")}: ${sum}% ${sum === 100 ? "" : `· ${t("a_weightsHint")}`}`} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("coverage")}>
            <Row gutter={12}>
              {num(["visits", "high"], t("risk_high"), 12)}
              {num(["visits", "medium"], t("risk_medium"), 12)}
              {num(["visits", "low"], t("risk_low"), 12)}
            </Row>
            <div style={{ fontSize: 12, opacity: 0.65 }}>{t("a_visitsHint")}</div>
            <Form.Item name="showRisk" valuePropName="checked" label={t("f_showRisk")} extra={t("f_showRiskHelp")} style={{ marginTop: 16, marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item name="vendorhubNewInactive" valuePropName="checked" label={t("act_vhNewInactive")} extra={t("act_vhNewInactiveHelp")} style={{ marginTop: 16, marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("esc30")}>
            <Form.Item name={["functionHead", "name"]} label={t("fullName")}><Input /></Form.Item>
            <Form.Item name={["functionHead", "email"]} label={t("email")} rules={[{ type: "email", message: t("a_emailInvalid") }]}><Input /></Form.Item>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("remRules")}>
            <Form.Item name="managerEsc" valuePropName="checked" label={t("a_managerEsc")} extra={t("a_managerEscHint")}>
              <Switch />
            </Form.Item>
            <ul style={{ fontSize: 13, lineHeight: 1.9, paddingLeft: 18, margin: 0 }}>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => <li key={i}>{t(`a_rule${i}`)}</li>)}
            </ul>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("d_rulesTitle")}>
            <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 12 }}>{t("d_rulesHelp")}</div>
            <Form.Item name={["zeroRule", "onNcCritical"]} label={t("d_onNcCritical")} extra={<span>{t(`d_preview_critical_${zr?.onNcCritical ?? "ratingD"}`)}</span>}>
              <Select options={ZERO_ACTIONS.map((a) => ({ value: a, label: t(`d_act_${a}`) }))} />
            </Form.Item>
            <Form.Item name={["zeroRule", "onZeroTolerance"]} label={t("d_onZeroTolerance")} extra={<span>{t(`d_preview_zero_${zr?.onZeroTolerance ?? "phaseout"}`)}</span>}>
              <Select options={ZERO_ACTIONS.map((a) => ({ value: a, label: t(`d_act_${a}`) }))} />
            </Form.Item>
            <div style={{ fontSize: 12, opacity: 0.65 }}>{t("d_rulesFoot")}</div>
            <Form.Item name="supplierRating" label={t("f_supplierRating")} extra={t("f_supplierRatingHelp")} style={{ marginTop: 16, marginBottom: 0 }}>
              <Select options={SUPPLIER_RATING_RULES.map((r) => ({ value: r, label: t(`f_rule_${r}`) }))} />
            </Form.Item>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={t("d_codeTitle")}>
            <Form.Item name="auditCodeYear" extra={t("d_codeHelp")}>
              <Radio.Group>
                <Radio value="current">{t("d_codeCurrent")}</Radio>
                <Radio value="planned">{t("d_codePlanned")}</Radio>
              </Radio.Group>
            </Form.Item>
          </Card>
        </Col>
        <Col xs={24}>
          <Card title={t("d_retentionTitle")}>
            <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 12 }}>{t("d_retentionHelp")}</div>
            <Row gutter={12}>
              <Col xs={24} sm={12} lg={6}><Form.Item name={["retention", "evidenceYears"]} label={t("d_retEvidence")} rules={[{ required: true, message: t("required") }]}><InputNumber min={1} max={100} style={{ width: "100%" }} /></Form.Item></Col>
              <Col xs={24} sm={12} lg={6}><Form.Item name={["retention", "auditLogYears"]} label={t("d_retLog")} rules={[{ required: true, message: t("required") }]}><InputNumber min={1} max={100} style={{ width: "100%" }} /></Form.Item></Col>
              <Col xs={24} sm={12} lg={6}><Form.Item name={["retention", "closedAuditYears"]} label={t("d_retAudit")} rules={[{ required: true, message: t("required") }]}><InputNumber min={1} max={100} style={{ width: "100%" }} /></Form.Item></Col>
              <Col xs={24} sm={12} lg={6}><Form.Item name={["retention", "inactiveSupplierUserMonths"]} label={t("d_retUsers")} rules={[{ required: true, message: t("required") }]}><InputNumber min={1} max={240} style={{ width: "100%" }} /></Form.Item></Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <div style={{ marginTop: 16 }}>
        <Button type="primary" htmlType="submit" loading={save.isPending}>{t("save")}</Button>
      </div>
    </Form>
  );
}
